import React from 'react';

import { SkeletonBar } from 'src/components/TableNew';

import {
  ChartSkeleton,
  SkeletonCard,
  SkeletonCardRow,
  SkeletonField,
  SkeletonGrid,
  SkeletonRow,
  SkeletonWrap,
} from './styled';

export type ContentSkeletonVariant =
  | 'minimal'
  | 'fields'
  | 'userDetail'
  | 'chart'
  | 'cards'
  | 'detailModal';

type ContentSkeletonProps = {
  variant?: ContentSkeletonVariant;
  /** Number of field/card blocks for `fields` and `cards` variants. */
  count?: number;
  className?: string;
};

const FIELD_WIDTHS = ['58%', '82%', '44%', '68%', '36%', '74%'];

export const ContentSkeleton: React.FC<ContentSkeletonProps> = ({
  variant = 'minimal',
  count = 6,
  className,
}) => {
  if (variant === 'chart') {
    return <ChartSkeleton className={className} aria-hidden />;
  }

  if (variant === 'cards') {
    return (
      <SkeletonWrap className={className} $gap="1rem" $padding="1.25rem 0 0" aria-hidden>
        {Array.from({ length: count }, (_, index) => (
          <SkeletonCard key={index}>
            <SkeletonCardRow>
              <SkeletonBar $width="5rem" />
              <SkeletonBar $width="4rem" />
            </SkeletonCardRow>
            <SkeletonCardRow>
              <SkeletonBar $width="6rem" />
              <SkeletonBar $width="3.5rem" />
            </SkeletonCardRow>
          </SkeletonCard>
        ))}
      </SkeletonWrap>
    );
  }

  // Mirrors the detail modals: breadcrumb, headline with badges, then cards of
  // label/value pairs — so the placeholder occupies the same shape as the
  // content that replaces it.
  if (variant === 'detailModal') {
    return (
      <SkeletonWrap className={className} $gap="1.25rem" aria-hidden>
        <SkeletonBar $width="14rem" />
        <SkeletonRow $justify="space-between">
          <SkeletonWrap $gap="0.65rem">
            <SkeletonBar $width="11rem" />
            <SkeletonRow>
              <SkeletonBar $width="4.5rem" />
              <SkeletonBar $width="5.5rem" />
            </SkeletonRow>
          </SkeletonWrap>
          <SkeletonBar $width="7rem" />
        </SkeletonRow>

        {Array.from({ length: 2 }, (_, card) => (
          <SkeletonCard key={card}>
            <SkeletonBar $width="7rem" />
            <SkeletonGrid>
              {Array.from({ length: count }, (_, index) => (
                <SkeletonWrap key={index} $gap="0.5rem">
                  <SkeletonBar $width="4rem" />
                  <SkeletonBar $width={FIELD_WIDTHS[(card * count + index) % FIELD_WIDTHS.length]} />
                </SkeletonWrap>
              ))}
            </SkeletonGrid>
          </SkeletonCard>
        ))}
      </SkeletonWrap>
    );
  }

  if (variant === 'userDetail') {
    return (
      <SkeletonWrap className={className} $gap="1.25rem" aria-hidden>
        <SkeletonRow>
          <SkeletonBar $width="3.5rem" />
          <SkeletonBar $width="0.5rem" />
          <SkeletonBar $width="8rem" />
        </SkeletonRow>
        <SkeletonRow $justify="space-between">
          <SkeletonWrap $gap="0.65rem">
            <SkeletonBar $width="12rem" />
            <SkeletonRow>
              <SkeletonBar $width="4.5rem" />
              <SkeletonBar $width="5rem" />
            </SkeletonRow>
          </SkeletonWrap>
          <SkeletonWrap $gap="0.4rem" $padding="0">
            <SkeletonBar $width="6rem" />
            <SkeletonBar $width="8rem" />
          </SkeletonWrap>
        </SkeletonRow>
        <SkeletonRow>
          {Array.from({ length: 5 }, (_, index) => (
            <SkeletonBar key={index} $width="5.5rem" />
          ))}
        </SkeletonRow>
        <SkeletonGrid>
          {Array.from({ length: count }, (_, index) => (
            <SkeletonField key={index}>
              <SkeletonBar $width="4rem" />
              <SkeletonBar $width={FIELD_WIDTHS[index % FIELD_WIDTHS.length]} />
            </SkeletonField>
          ))}
        </SkeletonGrid>
      </SkeletonWrap>
    );
  }

  if (variant === 'fields') {
    return (
      <SkeletonWrap className={className} $padding="0.5rem 0 0" aria-hidden>
        <SkeletonGrid>
          {Array.from({ length: count }, (_, index) => (
            <SkeletonField key={index}>
              <SkeletonBar $width="4rem" />
              <SkeletonBar $width={FIELD_WIDTHS[index % FIELD_WIDTHS.length]} />
            </SkeletonField>
          ))}
        </SkeletonGrid>
      </SkeletonWrap>
    );
  }

  return (
    <SkeletonWrap className={className} $gap="0.65rem" aria-hidden>
      <SkeletonBar $width="6rem" />
      <SkeletonBar $width="10rem" />
      <SkeletonBar $width="7.5rem" />
    </SkeletonWrap>
  );
};

export { ChartSkeleton };
